import { get } from './api.service';

export interface MarketQuote {
  price: number;
  change: number;
  changePercent: number;
  lastUpdated?: string;
}

export interface KiteStatus {
  tokenValid: boolean;
  loginTime: string | null;
  expiresAt: string | null;
  isConnected: boolean;
  subscribedCount: number;
  lastTickAt: string | null;
  updatedAt: string | null;
}

export interface Instrument {
  tradingsymbol: string;
  instrument_token: number;
  exchange: string;
  instrument_type: string; // EQ, FUT, CE, PE
  name: string;
  lot_size: number;
  expiry?: string;
  strike?: number;
  segment: string;
}

export interface FnOScrip {
  symbol: string;
  lotSize: number;
}

export interface BulkQuoteData {
  tradingsymbol: string;
  ltp: number;
  open: number;
  high: number;
  low: number;
  close: number; // prev close
  change: number;
  changePercent: number;
  volume: number;
  oi?: number;
  lastTradeTime?: string;
}

export interface OptionExpiry {
  expiry: string;        // YYYY-MM-DD
  underlyingCount: number;
}

export interface OptionsWatchlist {
  expiry: string;
  underlyingCount: number;
  symbolCount: number;
  txt: string;           // comma-separated TV symbols for paste
  csv: string;           // Underlying,Spot,Call_Strike,Put_Strike,CE_Symbol,PE_Symbol
  skipped: string[];
}

export const marketService = {
  async getOptionExpiries(): Promise<{ expiries: OptionExpiry[] }> {
    return get<{ expiries: OptionExpiry[] }>('/market/options-watchlist/expiries');
  },

  async getOptionsWatchlist(expiry: string): Promise<OptionsWatchlist> {
    return get<OptionsWatchlist>('/market/options-watchlist', { expiry });
  },

  async getQuotes(symbols: string[]): Promise<{ prices: Record<string, MarketQuote>; source?: string }> {
    const response = await get<{ prices: Record<string, MarketQuote>; source?: string }>('/market/quotes', {
      symbols: symbols.join(','),
    });
    return { prices: response.prices, source: (response as any).source };
  },

  async getKiteStatus(): Promise<KiteStatus> {
    const response = await get<KiteStatus>('/market/kite/status');
    return response;
  },

  getKiteLoginUrl(): string {
    return 'https://kite.zerodha.com/connect/login?v=3&api_key=wepj9q24twm6ojh3';
  },

  async getInstruments(params?: { exchange?: string; segment?: string; search?: string; underlying?: string }): Promise<{ instruments: Instrument[]; count: number; cachedAt: string }> {
    const queryParams: Record<string, unknown> = {};
    if (params?.exchange) queryParams.exchange = params.exchange;
    if (params?.segment) queryParams.segment = params.segment;
    if (params?.search) queryParams.search = params.search;
    if (params?.underlying) queryParams.underlying = params.underlying;
    const response = await get<{ instruments: Instrument[]; count: number; cachedAt: string }>('/market/instruments', queryParams);
    return response;
  },

  async getFnOScrips(): Promise<{ fnoScrips: FnOScrip[] }> {
    const response = await get<{ fnoScrips: FnOScrip[] }>('/market/fno-scrips');
    return response;
  },

  async getBulkQuotes(instruments: string[]): Promise<{ quotes: Record<string, BulkQuoteData>; count: number }> {
    const response = await get<{ quotes: Record<string, BulkQuoteData>; count: number }>('/market/bulk-quotes', {
      instruments: instruments.join(','),
    });
    return response;
  },
};
